import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, Trees, X } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', phone: '', area: '' };

export const QuickLandLeadModal = ({ isOpen, onClose, onFullForm }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!isOpen) return null;

  const close = () => { setForm(EMPTY); setDone(false); setError(''); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone.replace(/\D/g, '').length < 10) { setError('Enter a valid 10-digit mobile number.'); return; }
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/owner-leads`, form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail?.toString() || 'Could not submit. Please try again.');
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] bg-slate-900/60 grid place-items-center p-4" data-testid="quick-land-modal">
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl animate-popupScale">
        <button type="button" onClick={close} className="absolute right-4 top-4 text-slate-400 hover:text-slate-700" data-testid="quick-land-close"><X size={18} /></button>
        {done ? (
          <div className="text-center py-6" data-testid="quick-land-success">
            <CheckCircle2 size={44} className="mx-auto text-emerald-600" />
            <h3 className="mt-3 text-xl font-black text-slate-900">Thanks, {form.name.split(' ')[0]}!</h3>
            <p className="mt-1 text-sm text-slate-600">Our land expert will call you within 24 hours.</p>
            <Button className="mt-5 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={close} data-testid="quick-land-done">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-emerald-700 text-white grid place-items-center"><Trees size={20} /></div>
              <div>
                <h3 className="text-xl font-black text-slate-900">List your own land</h3>
                <p className="text-xs text-slate-500">3 quick details — we call you back.</p>
              </div>
            </div>
            <div><Label>Your name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quick-land-name" /></div>
            <div><Label>Mobile number</Label><Input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d+]/g, '') })} placeholder="10-digit mobile" data-testid="quick-land-phone" /></div>
            <div><Label>Land location & size</Label><Input required value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="e.g. Kalyani, 3 bigha" data-testid="quick-land-area" /></div>
            {error && <p className="text-xs text-red-600" data-testid="quick-land-error">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="quick-land-submit">
              {busy && <Loader2 size={14} className="mr-1 animate-spin" />} Request a callback
            </Button>
            <button type="button" onClick={() => { close(); onFullForm(); }} className="w-full text-xs font-semibold text-emerald-700 underline underline-offset-2" data-testid="quick-land-full-form">
              Prefer the full listing form with photos?
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
};
